import type { CarNote } from '../game/carNotes';
import type { LapResult, MedalName, SectorColor } from '../game/session';
import type { MedalTimes } from '../track/types';
import { el, formatDelta, formatLap } from './format';

const RADIO_GLYPH =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11z"/></svg>';

const MEDAL_LABEL: Record<MedalName, string> = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', author: 'Author' };

/** What the watching caption says, and where its button goes: your turn next, or (a past day's lap) back to today. */
/** A banner's button: its label, and what a tap (or its key) does, which can say how it went on the button. */
export interface BannerAction {
  label: string;
  onTap: (say: (label: string) => void) => void;
}

export interface WatchWords {
  text: string;
  skip: string;
  /** The red label at the line. */
  turn: string;
  /** The button once the lap is over. */
  after: string;
}

/** Stuck or crashed, with keys or a controller: the key(s) to press, and (the first time) what it does. */
export interface RescueRow {
  keys: string[];
  /** Null: just the keys, glowing (they've been told before). */
  words: string | null;
}

export const RACE_NEXT: WatchWords = {
  text: 'you can’t drive yet · your turn starts after this lap',
  skip: 'skip to my turn ▸',
  turn: 'your turn!',
  after: 'race now ▸',
};

/** Yesterday's winner is for watching only. */
export const WATCH_ONLY: WatchWords = {
  text: 'yesterday’s winning lap · just for watching',
  skip: 'back to today ▸',
  turn: 'yesterday’s winner',
  after: 'back to today ▸',
};

/**
 * A HUD button that works mid-race: on the lift of the finger, not only on
 * "click", because iOS drops the click while another finger is on the screen
 * (a thumb on the gas). The click still covers mouse and keyboard.
 */
function chip(parent: HTMLElement, cls: string, text: string, fire: () => void): HTMLButtonElement {
  const b = el('button', cls, parent, text);
  b.type = 'button';
  let tappedAt = -1e9;
  b.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'mouse') return;
    tappedAt = performance.now();
    fire();
  });
  // And no click after the tap: what the button opens (the poster) is under the finger by then.
  b.addEventListener('touchend', (e) => e.preventDefault(), { passive: false });
  b.onclick = () => {
    if (performance.now() - tappedAt > 700) fire();
  };
  return b;
}

/** Poster-style HUD: circuit name top-left, lap time in a cream box top-right. */
export class Hud {
  readonly root: HTMLElement;
  private nameEl: HTMLElement;
  private subEl: HTMLElement;
  private timebox: HTMLElement;
  private digits: HTMLElement[] = [];
  private sectors: HTMLElement[] = [];
  private deltaEl: HTMLElement;
  private bestEl: HTMLElement;
  private rankEl: HTMLElement;
  private saveChip: HTMLButtonElement;
  onSaveChip: () => void = () => {};
  private rivalEl: HTMLElement;
  private watchEl: HTMLElement;
  private watchCard: HTMLElement;
  private nameBlock: HTMLElement;
  private timeBlock: HTMLElement;
  private watchTitle: HTMLElement;
  private watchText: HTMLElement;
  private watchSkip: HTMLButtonElement;
  private watchTurn: HTMLElement;
  private watchWords: WatchWords = RACE_NEXT;
  private watchKeys: HTMLElement;
  private watchFill: HTMLElement;
  private watchPeers: HTMLElement;
  private tagsEl: HTMLElement;
  private tags: HTMLElement[] = [];
  /** Skip the replay being watched. */
  onSkip: () => void = () => {};
  /** Follow another of the laps watched together (by its key). */
  onPeer: (key: number) => void = () => {};
  /** Hide (or show again) your own ghost in a replay. */
  onOwnGhost: () => void = () => {};
  /** Racing a friend's lap: its ✕, to race on your own. */
  onDropRival: () => void = () => {};
  private ownChip: HTMLButtonElement;
  private watchVersus: HTMLElement;
  private watchBadge: HTMLElement;
  /** Driving while friends in the Discord server watch: who, in red under the clock. */
  private watchersEl: HTMLElement;
  /** The row at the foot of a replay (your ghost, the way out); watching live, the other drivers join it. */
  private watchRow: HTMLElement;
  /** The circuit's name and line, kept while a friend's name stands in their place (watching live). */
  private circuitWords: { name: string; sub: Node[] } | null = null;
  private medalEls = new Map<MedalName, HTMLElement>();
  private banner: HTMLElement;
  private bannerMain: HTMLElement;
  private bannerSmall: HTMLElement;
  private bannerTimer = 0;
  private card: HTMLElement;
  private cardTimer = 0;
  /** A first clean lap's stamp ("clean lap!", its time, where it ranks), its confetti, and what it held back. */
  private stamp: HTMLElement;
  private stampLine: HTMLElement;
  private stampTimer = 0;
  private stampParts: { time: string; medal: string | null; rank: string | null } = { time: '', medal: null, rank: null };
  private confetti: Confetti | null = null;
  private heldBanner: [string, string, 'bad' | 'good' | 'gold' | '', number, BannerAction | undefined] | null = null;
  /** A rebuilt car's team radio (CAR_NOTES), and what "got it" does while it's up. */
  private note: HTMLElement;
  private noteDone: (() => void) | null = null;
  /** The banner's button, while it's up ("send it back"). */
  private bannerAct: { el: HTMLButtonElement; tap: () => void } | null = null;
  private glowTimer = 0;
  private hintEl: HTMLElement;
  private rescueEl: HTMLElement;
  readonly fpsEl: HTMLElement;
  private lastText = '';

  constructor(parent: HTMLElement) {
    this.root = el('div', 'hud', parent);
    const name = el('div', 'hud-name', this.root);
    this.nameBlock = name;
    this.nameEl = el('h1', '', name);
    this.subEl = el('div', 'sub', name);

    const right = el('div', 'hud-time', this.root);
    this.timeBlock = right;
    this.timebox = el('div', 'timebox idle', right);
    // m:ss.mmm with fixed-width digit cells so the time doesn't jitter.
    for (const ch of '0:00.000') {
      const span = el('span', ch === ':' || ch === '.' ? 'p' : 'd', this.timebox, ch);
      this.digits.push(span);
    }
    const sec = el('div', 'sectors', right);
    for (let i = 0; i < 3; i++) this.sectors.push(el('div', 'sector', sec));
    this.deltaEl = el('div', 'delta hidden', right, '+0.000');
    this.bestEl = el('div', 'best', right);
    this.rankEl = el('div', 'rank hidden', right);
    this.rivalEl = el('div', 'rival hidden', right);
    // Right under the clock, where the eye already goes.
    this.watchersEl = el('div', 'watchers', right);
    right.insertBefore(this.watchersEl, this.deltaEl);
    this.watchersEl.hidden = true;
    this.saveChip = chip(right, 'save-chip interactive hidden', 'Save to leaderboard ▸', () => this.onSaveChip());
    // Watching someone's lap: a caption that says so (no driving yet), a big way out, and how far round it is.
    this.watchEl = el('div', 'watch-layer', this.root);
    const card = el('div', 'watch-card', this.watchEl);
    this.watchCard = card;
    this.watchTitle = el('div', 'title', card);
    this.watchText = el('div', 'text', card);
    // Today's champion's lap: why their car is gold.
    this.watchBadge = el('div', 'champ-line', card);
    this.watchBadge.hidden = true;
    // At the line, with your ghost out: your lap against theirs, sector by sector.
    this.watchVersus = el('div', 'versus', card);
    this.watchVersus.hidden = true;
    this.watchTurn = el('div', 'turn', card);
    // Watching a podium: the three drivers, to follow another (the others run on as ghosts).
    this.watchPeers = el('div', 'peers', card);
    this.watchPeers.hidden = true;
    const foot = el('div', 'watch-foot', this.watchEl);
    const row = el('div', 'watch-row', foot);
    this.watchRow = row;
    this.ownChip = chip(row, 'own-ghost interactive', 'your ghost ✓', () => this.onOwnGhost());
    this.ownChip.hidden = true;
    this.watchSkip = chip(row, 'watch-skip interactive', 'skip to my turn ▸', () => this.onSkip());
    this.watchKeys = el('div', 'watch-keys', foot);
    const bar = el('div', 'watch-progress', this.watchEl);
    this.watchFill = el('div', 'fill', bar);
    // Name flags over the ghosts.
    // (Under the replay's card and buttons; shown racing too: friends' cars on the track in a Discord server.)
    this.tagsEl = el('div', 'ghost-tags', this.root);
    this.root.insertBefore(this.tagsEl, this.watchEl);
    // The card sits as high as it can: whenever it, the screen, or the corners beside it change size.
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(() => this.placeWatchCard());
      for (const e of [card, name, right, this.root]) ro.observe(e);
    }
    const medals = el('div', 'medals', right);
    for (const m of ['bronze', 'silver', 'gold', 'author'] as MedalName[]) {
      const e = el('div', `medal ${m}`, medals);
      el('i', '', e);
      el('span', '', e);
      this.medalEls.set(m, e);
    }

    this.banner = el('div', 'banner', this.root);
    this.bannerMain = el('div', 'main', this.banner);
    this.bannerSmall = el('div', 'small', this.banner);
    this.card = el('div', 'lapcard', this.root);
    this.note = el('div', 'radio-note', this.root);
    this.stamp = el('div', 'stamp', this.root);
    el('div', 'big', this.stamp, 'clean lap!');
    this.stampLine = el('div', 'line', this.stamp);
    this.hintEl = el('div', 'hint', this.root, 'R restart · M mute');
    this.rescueEl = el('div', 'rescue', this.root);
    this.fpsEl = el('div', 'fps', this.root);
    this.fpsEl.style.display = 'none';
  }

  /** The circuit's name, and the line under it (`narrow`: the shorter one upright phones show). */
  setCircuit(name: string, sub: string, narrow = sub): void {
    this.nameEl.textContent = name;
    this.subEl.replaceChildren();
    el('span', 'wide', this.subEl, sub);
    el('span', 'narrow', this.subEl, narrow);
  }

  setHint(text: string): void {
    this.hintEl.textContent = text;
  }

  /** At the foot of the screen: how to back out, or restart (game/rescue.ts); none, nothing. */
  setRescue(rows: RescueRow[]): void {
    this.rescueEl.replaceChildren();
    for (const r of rows) {
      const row = el('div', `row${r.words ? '' : ' glow'}`, this.rescueEl);
      r.keys.forEach((k, i) => {
        if (i > 0) el('span', 'or', row, 'or');
        el('span', 'cap', row, k);
      });
      if (r.words) el('span', 'words', row, r.words);
    }
  }

  setTime(ms: number | null, invalid: boolean): void {
    const text = ms === null ? '0:00.000' : formatLap(ms);
    if (text !== this.lastText) {
      // Allow for 10+ minute laps gracefully.
      if (text.length !== this.digits.length) {
        this.timebox.textContent = text;
        this.digits = [];
      } else {
        for (let i = 0; i < text.length; i++) if (this.digits[i].textContent !== text[i]) this.digits[i].textContent = text[i];
      }
      this.lastText = text;
    }
    this.timebox.classList.toggle('invalid', invalid);
    this.timebox.classList.toggle('idle', ms === null);
  }

  setSectors(colors: (SectorColor | null)[]): void {
    this.sectors.forEach((e, i) => {
      const c = colors[i];
      e.className = 'sector' + (c ? ' ' + c : '');
    });
  }

  /** The live gap; `who` says whose it is ("you", watching a lap with your ghost beside it). */
  setDelta(ms: number | null, who = ''): void {
    this.deltaEl.classList.toggle('vs', who !== '');
    if (ms === null || !Number.isFinite(ms)) {
      this.deltaEl.classList.add('hidden');
      return;
    }
    this.deltaEl.classList.remove('hidden');
    this.deltaEl.textContent = who ? `${who} ${ms < 0 ? '−' : '+'}${(Math.abs(ms) / 1000).toFixed(2)}` : formatDelta(ms);
    this.deltaEl.classList.toggle('faster', ms < -5);
    this.deltaEl.classList.toggle('slower', ms > 5);
  }

  setBest(ms: number | null): void {
    this.bestEl.innerHTML = ms === null ? 'Best lap <b>—</b>' : `Best lap <b>${formatLap(ms)}</b>`;
  }

  setMedals(times: MedalTimes, won: Record<MedalName, boolean>): void {
    for (const [m, e] of this.medalEls) {
      e.classList.toggle('won', won[m]);
      const span = e.querySelector('span');
      if (span) span.textContent = formatLap(times[m]).replace(/^0:/, '');
      e.title = `${MEDAL_LABEL[m]} ${formatLap(times[m])}`;
    }
  }

  /** A banner in the sky; `action`: a button under it, one tap (or its key) while the banner is up. */
  showBanner(main: string, small: string, style: 'bad' | 'good' | 'gold' | '' = '', seconds = 2.4, action?: BannerAction): void {
    // While a first clean lap's stamp is up it's the news: the latest banner waits for it.
    if (this.stampTimer > 0) {
      this.heldBanner = [main, small, style, seconds, action];
      return;
    }
    this.bannerMain.textContent = main;
    this.bannerSmall.textContent = small;
    this.banner.className = 'banner show ' + style;
    this.bannerTimer = seconds;
    this.setBannerAction(action ?? null);
    this.announcing();
  }

  private setBannerAction(a: BannerAction | null): void {
    this.bannerAct?.el.remove();
    this.bannerAct = null;
    if (!a) return;
    const b = el('button', 'act interactive', this.banner, a.label) as HTMLButtonElement;
    b.type = 'button';
    const tap = (): void => {
      if (this.bannerAct?.el !== b) return;
      a.onTap((label) => (b.textContent = label));
    };
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      tap();
    });
    this.bannerAct = { el: b, tap };
  }

  /** A rebuilt car: what changed, over the car before it moves, until "got it" (a tap, or closeNote) calls onDone. */
  showNote(n: CarNote, onDone: () => void): void {
    this.note.replaceChildren();
    const k = el('div', 'kicker', this.note);
    k.innerHTML = RADIO_GLYPH;
    el('span', '', k, 'team radio');
    el('div', 'title', this.note, n.title);
    el('p', 'body', this.note, n.body);
    el('div', 'tip', this.note, n.tip);
    const b = el('button', 'got interactive', this.note, 'got it ▸') as HTMLButtonElement;
    b.type = 'button';
    this.noteDone = () => {
      this.note.classList.remove('show');
      this.noteDone = null;
      onDone();
    };
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeNote();
    });
    this.note.className = 'radio-note show interactive';
  }

  /** "Got it" by its key (Enter, Space, a controller's A): true if the note was up. */
  closeNote(): boolean {
    if (!this.noteDone) return false;
    this.noteDone();
    return true;
  }

  /** The banner's button by its key (C): true if there was one to press. */
  pressBannerAction(): boolean {
    if (!this.bannerAct || this.bannerTimer <= 0) return false;
    this.bannerAct.tap();
    return true;
  }

  showLap(r: LapResult, colors: (SectorColor | null)[]): void {
    const d = r.previousBestMs !== null && r.valid ? formatDelta(r.ms - r.previousBestMs) : '';
    const splits = r.sectorMs
      .map((ms, i) => `<span class="${colors[i] ?? ''}">S${i + 1} ${(ms / 1000).toFixed(3)}</span>`)
      .join('');
    this.card.innerHTML = `<div class="t">${formatLap(r.ms)}</div>${d ? `<div class="d">${d}</div>` : ''}<div class="splits">${splits}</div>`;
    this.card.className = 'lapcard show';
    this.cardTimer = 3.2;
    this.announcing();
  }

  /**
   * A first clean lap: the stamp pops in (over the sky: on wide screens in the lap card's place, which it
   * stands in for), with confetti; after a few seconds it flies into the save chip. `medal`: one won with it.
   */
  celebrate(time: string, medal: string | null, pieces: number): void {
    this.stampParts = { time, medal, rank: null };
    this.drawStampLine();
    this.stamp.style.cssText = '';
    this.stamp.classList.remove('show');
    void this.stamp.offsetWidth;
    this.stamp.classList.add('show');
    this.stampTimer = STAMP_S;
    this.banner.classList.remove('show');
    this.bannerTimer = 0;
    this.setBannerAction(null);
    this.card.classList.remove('show');
    this.cardTimer = 0;
    this.confetti?.dispose();
    this.confetti = new Confetti(this.root, pieces);
    this.announcing();
  }

  /** Where the lap ranks, once the board has it ("P8 of 11 today"), if the stamp is still up. */
  setCelebrationRank(rank: string): void {
    if (this.stampTimer <= 0) return;
    this.stampParts.rank = rank;
    this.drawStampLine();
  }

  private drawStampLine(): void {
    const { time, medal, rank } = this.stampParts;
    this.stampLine.textContent = [time, rank, medal].filter(Boolean).join(' · ');
  }

  /** The stamp's last moment: into the save chip if it's there (it lights up as it lands), else up and away. */
  private flyStamp(): void {
    const s = this.stamp.style;
    const from = this.stamp.getBoundingClientRect();
    const to = this.saveChip.classList.contains('hidden') ? null : this.saveChip.getBoundingClientRect();
    const dx = to ? to.left + to.width / 2 - (from.left + from.width / 2) : 0;
    const dy = to ? to.top + to.height / 2 - (from.top + from.height / 2) : -from.bottom;
    s.animation = 'none';
    s.opacity = '1';
    s.transform = 'translate(-50%, 0) scale(1)';
    void this.stamp.offsetWidth;
    s.transition = `transform ${FLY_S}s cubic-bezier(0.5, 0, 0.75, 0), opacity ${FLY_S}s ease-in`;
    s.transform = `translate(calc(-50% + ${dx.toFixed(0)}px), ${dy.toFixed(0)}px) scale(0.15)`;
    s.opacity = '0';
  }

  /** While a banner, the lap card or the stamp is up, the "save to leaderboard" chip steps aside (on a phone they share the spot). */
  private announcing(): void {
    this.root.classList.toggle('announcing', this.bannerTimer > 0 || this.cardTimer > 0 || this.stampTimer > 0);
  }

  /** Your place on today's board ("P3 of 14"), or null to hide it. Flashes when it changes. */
  setRank(text: string | null): void {
    const was = this.rankEl.textContent;
    this.rankEl.classList.toggle('hidden', text === null);
    if (text === null) return;
    this.rankEl.textContent = `Today ${text}`;
    if (was && was !== this.rankEl.textContent) {
      this.rankEl.classList.remove('flash');
      void this.rankEl.offsetWidth;
      this.rankEl.classList.add('flash');
    }
  }

  /**
   * Watching a replay: whose lap ("camilo's"), said big so nobody reaches for
   * the controls; `keys` adds the way out without touching ("or press Esc",
   * "or press B"). Null when it's over.
   */
  setWatching(whose: string | null, keys: string | null = null, words: WatchWords = RACE_NEXT): void {
    this.root.classList.toggle('watching', whose !== null);
    this.watchEl.classList.remove('done');
    this.watchVersus.hidden = true;
    if (whose === null) return;
    this.watchWords = words;
    this.watchTitle.textContent = `watching ${whose} lap`;
    this.watchText.textContent = words.text;
    this.watchSkip.textContent = words.skip;
    this.watchTurn.textContent = words.turn;
    this.watchKeys.textContent = keys ?? '';
    this.watchKeys.hidden = !keys;
    this.setWatchProgress(0);
  }

  /**
   * The watch card as high as it goes, so it hides as little of the track as it can: up in the top row, between
   * the circuit's name and the time, when it fits there; otherwise just under whichever of the two it would cover.
   */
  private placeWatchCard(): void {
    const card = this.watchCard;
    const box = card.offsetParent?.getBoundingClientRect();
    if (!box || !card.offsetWidth) return;
    const name = this.nameBlock.getBoundingClientRect();
    const time = this.timeBlock.getBoundingClientRect();
    const left = box.left + box.width / 2 - card.offsetWidth / 2;
    const right = left + card.offsetWidth;
    const gap = 16;
    let top = Math.min(name.top, time.top);
    if (name.width && left < name.right + gap) top = Math.max(top, name.bottom + gap / 2);
    if (time.width && right > time.left - gap) top = Math.max(top, time.bottom + gap / 2);
    card.style.top = `${Math.round(top - box.top)}px`;
  }

  /** Watching a podium: who's being followed, and the others to switch to (null: not a podium). */
  setPeers(peers: { key: number; place: number | null; name: string; own: boolean; on: boolean }[] | null, fewest = 2): void {
    this.watchPeers.replaceChildren();
    this.watchPeers.hidden = !peers || peers.length < fewest;
    if (!peers || peers.length < fewest) return;
    for (const p of peers) {
      const b = chip(this.watchPeers, `peer interactive${p.on ? ' on' : ''}${p.own ? ' own' : ''}`, '', () => this.onPeer(p.key));
      if (p.place !== null) el('b', '', b, String(p.place));
      el('span', '', b, p.name);
      b.disabled = p.on;
      b.setAttribute('aria-label', p.on ? `Following ${p.name}` : `Follow ${p.name}`);
    }
  }

  /** The chip for your own ghost in a replay: shown (true), hidden (false), or no chip (you've no lap there). */
  setOwnGhost(on: boolean | null): void {
    this.ownChip.hidden = on === null;
    this.ownChip.textContent = on ? 'your ghost ✓' : 'your ghost';
    this.ownChip.classList.toggle('off', on === false);
    this.ownChip.setAttribute('aria-pressed', String(on === true));
  }

  /**
   * Name flags over the ghost cars, at their spots on screen (CSS pixels), fading with the cars; a ghost out
   * of sight (behind the camera, round a bend) has its flag pinned to the edge it's beyond ('behind': the foot
   * of the screen, 'left'/'right': the sides). With the gap to the car being followed, once the lap is on.
   */
  setGhostTags(tags: { place: number | null; name: string; gap: string | null; own?: boolean; gold?: boolean; live?: boolean; x: number; y: number; alpha: number; pinned: 'behind' | 'left' | 'right' | null }[]): void {
    while (this.tags.length < tags.length) {
      const t = el('div', 'ghost-tag', this.tagsEl);
      el('b', '', t);
      el('span', 'who', t);
      el('span', 'gap', t);
      this.tags.push(t);
    }
    this.tags.forEach((t, i) => {
      const g = tags[i];
      if (!g || g.alpha < 0.05) {
        t.style.display = 'none';
        return;
      }
      t.style.display = '';
      const [b, who, gap] = t.children as unknown as HTMLElement[];
      const mark = g.pinned === 'behind' ? '▾ ' : g.pinned === 'left' ? '◂ ' : g.pinned === 'right' ? '▸ ' : '';
      const label = `${mark}${g.place ?? ''}`;
      if (b.textContent !== label) b.textContent = label;
      b.hidden = label === '';
      if (who.textContent !== g.name) who.textContent = g.name;
      if (gap.textContent !== (g.gap ?? '')) gap.textContent = g.gap ?? '';
      gap.hidden = !g.gap;
      t.className = `ghost-tag${g.pinned ? ' pinned' : ''}${g.own ? ' own' : ''}${g.gold ? ' gold' : ''}${g.live ? ' live' : ''}`;
      t.style.transform = `translate(${g.x.toFixed(1)}px, ${g.y.toFixed(1)}px) translate(-50%, -100%)`;
      t.style.opacity = String(Math.min(1, g.alpha * 1.3));
    });
  }

  /** How far round the watched lap is, 0 to 1. */
  setWatchProgress(f: number): void {
    this.watchFill.style.transform = `scaleX(${Math.max(0, Math.min(1, f))})`;
  }

  /** The watched lap crossed the line: its time, whose it was, and the handover to you. */
  watchFinished(time: string, whose: string, versus: { line: string; split: number[]; note: string } | null = null): void {
    this.watchEl.classList.add('done');
    this.watchTitle.textContent = time;
    this.watchText.textContent = versus ? `${whose} lap · ${versus.line}` : `${whose} lap`;
    this.watchVersus.hidden = !versus;
    this.watchVersus.replaceChildren();
    if (versus) {
      const row = el('div', 'split', this.watchVersus);
      versus.split.forEach((d, k) => {
        const c = el('span', d > 5 ? 'lost' : d < -5 ? 'gained' : '', row);
        el('b', '', c, `S${k + 1}`);
        el('span', '', c, `${d < 0 ? '−' : '+'}${(Math.abs(d) / 1000).toFixed(2)}`);
      });
      el('div', 'note', this.watchVersus, versus.note);
    }
    this.watchSkip.textContent = this.watchWords.after;
    this.setWatchProgress(1);
  }

  /** Racing someone's lap: "vs camilo 41.843" under your best, or null. */
  /** The lap raced against; `champion`: yesterday's winner's (their ghost is gold), marked with a gold 1. */
  setRival(name: string | null, ms = 0, champion = false): void {
    this.rivalEl.classList.toggle('hidden', name === null);
    if (name === null) return;
    this.rivalEl.replaceChildren('vs ');
    if (champion) {
      const one = el('i', 'champ', this.rivalEl, '1');
      one.title = 'Yesterday’s winner: their car is gold today';
    }
    this.rivalEl.append(`${name} `);
    el('b', '', this.rivalEl, formatLap(ms).replace(/^0:/, ''));
    // One tap and it's gone: your own race again (your best's ghost comes back).
    const drop = chip(this.rivalEl, 'drop interactive', '✕', () => this.onDropRival());
    drop.setAttribute('aria-label', `Race without ${name}’s ghost`);
    drop.title = `Race without ${name}’s ghost`;
  }

  /**
   * Watching a friend drive right now (in a Discord server): no card over the road. Their name stands where the
   * circuit's is, with a red "live" before it and what they're up to under it ("lap 4"); the other drivers sit
   * beside the way out. Null: not watching live (the circuit's name back).
   */
  setLiveWatch(o: { name: string; text: string; skip: string } | null): void {
    this.root.classList.toggle('live', !!o);
    if (!o) {
      if (this.circuitWords) {
        this.nameEl.textContent = this.circuitWords.name;
        this.subEl.replaceChildren(...this.circuitWords.sub);
        this.circuitWords = null;
      }
      this.watchCard.appendChild(this.watchPeers);
      return;
    }
    this.watchRow.insertBefore(this.watchPeers, this.watchSkip);
    this.circuitWords ??= { name: this.nameEl.textContent ?? '', sub: [...this.subEl.childNodes] };
    this.nameEl.replaceChildren();
    const tag = el('span', 'live-tag', this.nameEl);
    el('i', '', tag);
    tag.append('live');
    this.nameEl.append(o.name.toLowerCase());
    this.subEl.replaceChildren(o.text);
    this.watchSkip.textContent = o.skip;
  }

  /** Watching live: what they're up to now, under their name ("lap 5", "paused"). */
  setLiveLine(text: string): void {
    if (this.circuitWords && this.subEl.textContent !== text) this.subEl.replaceChildren(text);
  }

  /** Friends watching you drive (their names), in red under the clock; none, nothing. Someone new: it pops in once. */
  setWatchers(names: string[]): void {
    const before = this.watchersEl.hidden ? 0 : Number(this.watchersEl.dataset.n ?? 0);
    this.watchersEl.hidden = names.length === 0;
    this.watchersEl.replaceChildren();
    this.watchersEl.dataset.n = String(names.length);
    if (!names.length) return;
    el('i', 'eye', this.watchersEl);
    el('b', '', this.watchersEl, names.length > 2 ? `${names.slice(0, 2).join(' · ')} +${names.length - 2}` : names.join(' · '));
    this.watchersEl.append(' watching');
    if (names.length > before) {
      this.watchersEl.classList.remove('pop');
      void this.watchersEl.offsetWidth;
      this.watchersEl.classList.add('pop');
    }
  }

  /** Watching yesterday's winner's lap: a gold line on the watch card saying why their car is gold (null: none). */
  setWatchBadge(text: string | null): void {
    this.watchBadge.hidden = text === null;
    this.watchBadge.textContent = text ?? '';
  }

  /**
   * The nudge to put a first best lap on the board, or (`madeUp`, a name the game gave them) to put their own
   * name on the one that's there.
   */
  setSavePrompt(on: boolean, madeUp: string | null = null): void {
    this.saveChip.classList.toggle('hidden', !on);
    const text = madeUp ? `✎ that’s you · ${madeUp} · rename` : 'Save to leaderboard ▸';
    if (this.saveChip.textContent !== text) this.saveChip.textContent = text;
  }

  medalName(m: MedalName): string {
    return MEDAL_LABEL[m];
  }

  tick(dt: number): void {
    if (this.bannerTimer > 0) {
      this.bannerTimer -= dt;
      if (this.bannerTimer <= 0) {
        this.banner.classList.remove('show');
        this.setBannerAction(null);
      }
    }
    if (this.cardTimer > 0) {
      this.cardTimer -= dt;
      if (this.cardTimer <= 0) this.card.classList.remove('show');
    }
    if (this.stampTimer > 0) {
      const before = this.stampTimer;
      this.stampTimer -= dt;
      if (before > FLY_S && this.stampTimer <= FLY_S) this.flyStamp();
      if (this.stampTimer <= 0) {
        this.stamp.classList.remove('show');
        this.stamp.style.cssText = '';
        // The chip lights up as the stamp lands in it; then whatever banner waited.
        if (!this.saveChip.classList.contains('hidden')) {
          this.saveChip.classList.add('glow');
          this.glowTimer = 1.4;
        }
        const held = this.heldBanner;
        this.heldBanner = null;
        if (held) this.showBanner(...held);
      }
    }
    if (this.glowTimer > 0) {
      this.glowTimer -= dt;
      if (this.glowTimer <= 0) this.saveChip.classList.remove('glow');
    }
    if (this.confetti && !this.confetti.tick(dt)) {
      this.confetti.dispose();
      this.confetti = null;
    }
    this.announcing();
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
  }

  /** Title screen: keep only the circuit name. */
  setTitleMode(on: boolean): void {
    this.root.classList.toggle('title-mode', on);
  }
}

/** How long a first clean lap's stamp stays up (s), the last part of it flying into the save chip. */
const STAMP_S = 3.6;
const FLY_S = 0.45;

/**
 * Confetti in the game's colours, thrown in from both top corners and up out of the stamp, fluttering down
 * and gone in about four seconds. A canvas at one pixel per CSS pixel and a few hundred rectangles: cheap.
 */
class Confetti {
  private canvas: HTMLCanvasElement;
  private g: CanvasRenderingContext2D | null;
  private t = 0;
  private bits: { x: number; y: number; vx: number; vy: number; w: number; h: number; spin: number; a: number; col: string; delay: number; flutter: number }[];
  private w: number;
  private h: number;

  constructor(parent: HTMLElement, count: number) {
    this.canvas = el('canvas', 'confetti', parent) as HTMLCanvasElement;
    this.w = window.innerWidth;
    this.h = window.innerHeight;
    this.canvas.width = this.w;
    this.canvas.height = this.h;
    this.g = this.canvas.getContext('2d');
    const cols = ['#B8352B', '#F3E6C8', '#EDC27A', '#8E9A58', '#2F4A2E', '#C8784A'];
    const tall = this.h > this.w;
    const top = tall ? 0.29 : 0.12;
    const speed = Math.max(this.w, this.h) / 1280;
    this.bits = Array.from({ length: count }, (_, i) => {
      const from = i % 3;
      const x = from === 0 ? -10 : from === 1 ? this.w + 10 : this.w / 2 + (Math.random() - 0.5) * this.w * 0.2;
      const y = from === 2 ? this.h * top : this.h * (0.04 + Math.random() * 0.2);
      const ang = from === 2 ? -Math.PI / 2 + (Math.random() - 0.5) * 2.2 : from === 0 ? -0.5 + Math.random() * 0.7 : Math.PI + 0.5 - Math.random() * 0.7;
      const sp = (from === 2 ? 9 + Math.random() * 9 : 11 + Math.random() * 12) * speed;
      return {
        x,
        y,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        w: (tall ? 6 : 8) + Math.random() * (tall ? 6 : 9),
        h: (tall ? 3 : 4) + Math.random() * 4,
        spin: (Math.random() - 0.5) * 0.5,
        a: Math.random() * 6.28,
        col: cols[i % cols.length],
        delay: Math.random() * 0.2,
        flutter: Math.random() * 6.28,
      };
    });
  }

  /** Draw the next moment; false once it's all fallen. */
  tick(dt: number): boolean {
    this.t += dt;
    const g = this.g;
    if (!g) return false;
    g.clearRect(0, 0, this.w, this.h);
    let alive = false;
    for (const p of this.bits) {
      // In frames at 30 a second, as it was drawn first.
      const k = (this.t - p.delay) * 30;
      if (k < 0) {
        alive = true;
        continue;
      }
      const x = p.x + (p.vx * (1 - Math.pow(0.94, k))) / 0.06 + Math.sin(k * 0.18 + p.flutter) * 6;
      const y = p.y + (p.vy * (1 - Math.pow(0.94, k))) / 0.06 + 0.055 * k * k * (this.h / 720);
      const fade = Math.max(0, Math.min(1, (130 - k) / 30));
      if (y > this.h + 20 || fade <= 0) continue;
      alive = true;
      g.save();
      g.globalAlpha = fade;
      g.translate(x, y);
      g.rotate(p.a + p.spin * k);
      g.scale(1, Math.abs(Math.cos(k * 0.21 + p.flutter)) * 0.85 + 0.15);
      g.fillStyle = p.col;
      g.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      g.restore();
    }
    return alive;
  }

  dispose(): void {
    this.canvas.remove();
  }
}
